import { useState } from "react";
import { Calculator, AlertTriangle } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { eur } from "../lib/format";
import { measuredQuantity, quantityForBase, reviewMeasurement, reviewPatch, unitBase } from "../lib/invoiceReview";

const inputStyle = "h-10 w-full min-w-0 rounded-md border border-[#CDD7E3] bg-white px-2 text-sm text-[#0B1F33]";
const labels = { u: "pièce", kg: "kg", L: "L" };
const number = (value) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 }).format(value);

export default function InvoiceLinePricing({ line, index, onChange }) {
  const measurement = reviewMeasurement(line);
  const [count, setCount] = useState("");
  const [size, setSize] = useState("");
  const [sizeUnit, setSizeUnit] = useState(measurement.base || "u");
  const calculated = measuredQuantity(count, size, sizeUnit);
  const change = (patch) => onChange(reviewPatch(line, patch));
  const originalTotal = line.review_original?.line_total_ht;
  const differs = originalTotal != null && Math.abs(Number(originalTotal) - Number(line.line_total_ht)) > 0.005;
  if (line.line_kind === "charge") return <div className="mt-3 space-y-2">
    <p className="text-xs text-[#5E6875]">Frais de facture · hors catalogue produits</p>
    <label className="block text-xs text-[#5E6875]">Total HT (€)
      <Input type="number" min="0" step="0.01" className="mt-1 !h-10" value={line.line_total_ht ?? ""}
        onChange={(e) => onChange({ line_total_ht: e.target.value === "" ? "" : Number(e.target.value) })} />
    </label>
  </div>;
  return (
    <div className="mt-3 min-w-0 space-y-3" data-testid={`line-pricing-${index}`}>
      <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
        <label className="min-w-0 text-xs text-[#5E6875]">
          Quantité totale achetée
          <Input className="mt-1 !h-10 w-full min-w-0" type="number" min="0" step="any"
            value={measurement.quantity ?? ""} aria-label="Quantité totale achetée"
            data-testid={`line-qty-${index}`}
            onChange={(e) => change({ reviewed_quantity: e.target.value === "" ? null : Number(e.target.value) })} />
        </label>
        <label className="min-w-0 text-xs text-[#5E6875]">
          Unité de référence
          <select className={`${inputStyle} mt-1`} value={measurement.base} aria-label="Unité de référence"
            onChange={(e) => change({ reviewed_base_unit: e.target.value,
              reviewed_quantity: quantityForBase(line, e.target.value) })}>
            <option value="" disabled>À vérifier</option>
            <option value="u">Pièce</option><option value="kg">Kilogramme</option><option value="L">Litre</option>
          </select>
        </label>
        <label className="min-w-0 text-xs text-[#5E6875]">
          Total payé HT (€)
          <Input className="mt-1 !h-10 w-full min-w-0" type="number" min="0" step="0.01"
            value={line.line_total_ht ?? ""} aria-label="Total payé HT"
            onChange={(e) => change({ line_total_ht: e.target.value === "" ? "" : Number(e.target.value) })} />
        </label>
        <label className="min-w-0 text-xs text-[#5E6875]">
          Prix HT / {labels[measurement.base] || "unité"}
          <Input className="mt-1 !h-10 w-full min-w-0 !bg-[#FFF4D6] font-semibold" type="number" min="0" step="any"
            value={measurement.price == null ? "" : Number(measurement.price.toFixed(6))}
            disabled={!measurement.quantity} aria-label="Prix de référence HT"
            onChange={(e) => change({ reviewed_price_ht: e.target.value === "" ? null : Number(e.target.value), line_total_ht: e.target.value === "" ? "" :
              Math.round(Number(e.target.value) * measurement.quantity * 100) / 100 })} />
        </label>
      </div>
      {measurement.valid ? (
        <p className="text-sm font-semibold text-[#0B1F33] break-words" data-testid={`line-price-result-${index}`}>
          {number(measurement.quantity)} {measurement.base === "u" ? "pièces" : labels[measurement.base]}
          {" · "}{eur(measurement.total)} HT{" → "}{eur(measurement.price, 4)} / {labels[measurement.base]}
        </p>
      ) : <p role="status" className="text-sm text-[#B36B00]">Quantité, unité ou total HT à vérifier.</p>}
      {differs && <p role="status" className="flex items-start gap-2 text-xs text-[#B36B00]">
        <AlertTriangle className="h-4 w-4 shrink-0" />Total de la facture avant correction : {eur(originalTotal)} HT.
      </p>}
      <details className="border-t border-[#E4E9F0] pt-2 min-w-0">
        <summary className="cursor-pointer text-xs font-medium text-[#1769CD]">Détail de quantité</summary>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-0">
          <label className="text-xs text-[#5E6875] min-w-0">Nombre
            <Input className="mt-1 !h-10 min-w-0 w-full" type="number" min="0" step="1" value={count}
              aria-label="Nombre" onChange={(e) => setCount(e.target.value)} /></label>
          <label className="text-xs text-[#5E6875] min-w-0">Quantité par élément
            <Input className="mt-1 !h-10 min-w-0 w-full" type="number" min="0" step="any" value={size}
              aria-label="Quantité par élément" onChange={(e) => setSize(e.target.value)} /></label>
          <label className="text-xs text-[#5E6875] min-w-0">Mesure
            <select className={`${inputStyle} mt-1`} value={sizeUnit} aria-label="Mesure"
              onChange={(e) => setSizeUnit(e.target.value)}>
              <option value="u">Pièces</option><option value="kg">kg</option><option value="g">g</option>
              <option value="L">L</option><option value="cl">cl</option><option value="ml">ml</option>
            </select></label>
          <Button type="button" className="self-end h-10" variant="outline"
            disabled={!calculated || (unitBase(sizeUnit) === "u" && !Number.isInteger(calculated))}
            onClick={() => change({ reviewed_quantity: calculated, reviewed_base_unit: unitBase(sizeUnit) })}>
            <Calculator className="w-4 h-4 mr-1" />Appliquer
          </Button>
        </div>
        {calculated && <p className="mt-2 text-xs text-[#5E6875]">{count} × {size} {sizeUnit} = {number(calculated)} {labels[unitBase(sizeUnit)]}</p>}
      </details>
      {measurement.base === "u" && line.content_role !== "capacity" && <details className="min-w-0">
        <summary className="cursor-pointer text-xs font-medium text-[#1769CD]">Contenance d’une pièce</summary>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Input type="number" className="!h-10 min-w-0 w-full" min="0" step="any" aria-label="Contenance d’une pièce"
            value={line.content_value ?? ""} onChange={(e) => change({ content_value: e.target.value === "" ? null : Number(e.target.value) })} />
          <select className={inputStyle} value={line.content_unit || ""} aria-label="Unité de contenance"
            onChange={(e) => change({ content_unit: e.target.value || null })}>
            <option value="">Non renseignée</option><option value="g">g</option><option value="kg">kg</option>
            <option value="cl">cl</option><option value="ml">ml</option><option value="L">L</option>
          </select>
        </div>
      </details>}
    </div>
  );
}
